import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal
} from '@angular/core';
import {
  DynamicSectionStandaloneComponent,
  SectionConfig
} from '@konnektu/dynamic/section';
import {
  injectShowTextTuiAlert,
  injectShowTextTuiPrompt
} from '@konnektu/helpers';
import { MetastoreService } from '@konnektu/metastore';
import { injectTenantNavigate } from '@konnektu/multitenant';
import { catchError, filter, switchMap, take, tap, throwError } from 'rxjs';
import { injectDynamicViewDefaultsDialog } from '../shared/dynamic-view-defaults-dialog/inject-dynamic-view-defaults-dialog';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
@Component({
  selector: 'knk-dynamic-view-section',
  imports: [DynamicSectionStandaloneComponent, TranslateModule],
  templateUrl: './dynamic-view-section.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DynamicViewSectionComponent {
  private readonly tenantNavigate = injectTenantNavigate();
  private readonly translate = inject(TranslateService);
  private readonly metastore = inject(MetastoreService);
  private readonly openPrompt = injectShowTextTuiPrompt();
  private readonly showTextAlert = injectShowTextTuiAlert();
  private readonly openDefaultsDialog = injectDynamicViewDefaultsDialog();

  protected readonly refreshTrigger = signal(0);

  protected readonly config: SectionConfig = {
    entityName: 'UIDynamicView',
    columns: [
      {
        id: 'Id',
        header: 'ID',
        sortable: true
      },
      {
        id: 'Type',
        header: this.translate.instant('dynamicEditor.type'),
        sortable: true
      },
      {
        id: 'Path',
        header: this.translate.instant('dynamicEditor.path'),
        sortable: true
      },
      {
        id: 'CreatedOn',
        header: this.translate.instant('dynamicEditor.createdOn'),
        type: 'date',
        sortable: true
      },
      {
        id: 'ModifiedOn',
        header: this.translate.instant('dynamicEditor.modifiedOn'),
        type: 'date',
        sortable: true
      }
    ],
    showSearch: true,
    searchColumns: ['Path', 'Type'],
    tableSize: 'm',
    showFilters: false,
    showRecordCount: false,
    initialSort: { ModifiedOn: 'desc' },
    rowActions: [
      {
        id: 'delete',
        text: '',
        icon: '@tui.trash',
        onClick: (row: any) => this.deleteView(row.Id)
      }
    ],
    topContent: {
      type: 'stack',
      orientation: 'horizontal',
      gap: 'sm',
      items: [
        {
          type: 'button',
          iconStart: '@tui.plus',
          items: [
            {
              label: this.translate.instant('dynamicEditor.card'),
              iconStart: '@tui.id-card',
              onClick: () => {
                this.tenantNavigate(['dynamic-views', 'card', 'new']);
              }
            },
            {
              label: this.translate.instant('dynamicEditor.section'),
              iconStart: '@tui.table-properties',
              onClick: () => {
                this.tenantNavigate(['dynamic-views', 'section', 'new']);
              }
            }
          ],
          label: this.translate.instant('common.create')
        },
        {
          type: 'button',
          iconStart: '@tui.settings',
          appearance: 'secondary',
          label: this.translate.instant('dynamicEditor.defaults'),
          onClick: () => this.openDefaultsDialog()
        }
      ]
    },
    rowClick: (event) => {
      const row = event.row as { Id: string; Type: string };
      if (row.Type === 'card') {
        this.tenantNavigate(['dynamic-views', 'card', row.Id]);
      } else if (row.Type === 'section') {
        this.tenantNavigate(['dynamic-views', 'section', row.Id]);
      }
    }
  };

  private deleteView(id: string) {
    return this.openPrompt(
      'dynamicEditor.removeViewConfirm',
      'common.remove',
      'common.cancel'
    ).pipe(
      filter(Boolean),
      switchMap(() => this.metastore.deleteByGuid('UIDynamicView', id)),
      take(1),
      tap(() => {
        this.showTextAlert('dynamicEditor.viewRemoveSuccess', 'positive');
        this.refreshTrigger.update((v) => v + 1);
      }),
      catchError((err) => {
        this.showTextAlert('dynamicEditor.removeError', 'negative');
        return throwError(() => err);
      })
    );
  }
}
